import { ArrowUpward } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { motion } from 'motion/react';
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

function getScrollParent(element: Element): Element | null {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export function StickyHeader({
  title,
  scrollingHeaderExtra,
  topRef,
  subtitleSentinelRef,
}: {
  title: string;
  scrollingHeaderExtra?: ReactNode;
  topRef: RefObject<HTMLDivElement | null>;
  subtitleSentinelRef: RefObject<HTMLDivElement | null>;
}) {
  const isMobile = useIsMobile('md');
  const scrollingToTopRef = useRef(false);
  const [showScrollingHeader, setShowScrollingHeader] = useState(false);

  useEffect(() => {
    const sentinel = subtitleSentinelRef.current;
    const topEl = topRef.current;
    if (!sentinel || !topEl) return;

    const scrollParent = getScrollParent(sentinel);
    const root = scrollParent ?? undefined;
    const HYSTERESIS_PX = 12;
    const HEADER_HEIGHT_PX = 50;
    // Fire "at top" when we're within this many px of the top so we collapse the header
    // *before* reaching scrollTop 0, avoiding CLS (content jump)
    const AT_TOP_BAND_PX = HEADER_HEIGHT_PX + 8;

    // "At top": when topEl enters the top band of the viewport — hide header early so
    // by the time we reach scrollTop 0 the layout is already collapsed and no jump
    const atTopObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        scrollingToTopRef.current = false;
        setShowScrollingHeader(false);
      },
      {
        root,
        rootMargin: `${AT_TOP_BAND_PX}px 0px -100% 0px`,
        threshold: 0,
      },
    );
    atTopObserver.observe(topEl);

    // Sticky header: show when sentinel has scrolled past (visibleTop - 12px), hide in hysteresis zone when well below
    const sentinelObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (scrollingToTopRef.current) return;

        const rootTop = entry.rootBounds?.top ?? 0;
        const sentinelBottom = entry.boundingClientRect.bottom;

        if (!entry.isIntersecting) {
          // Sentinel is above the (visibleTop - 12px) line → scrolled past → show header
          setShowScrollingHeader(true);
          return;
        }

        // rootBounds.top is (scroll container top + 12px); hide when sentinel is below visibleTop + 12px
        if (sentinelBottom > rootTop) {
          setShowScrollingHeader(false);
        }
      },
      {
        root,
        rootMargin: `-${HYSTERESIS_PX}px 0px 0px 0px`,
        threshold: 0,
      },
    );
    sentinelObserver.observe(sentinel);

    return () => {
      atTopObserver.disconnect();
      sentinelObserver.disconnect();
    };
  }, []);

  const scrollToTop = () => {
    scrollingToTopRef.current = true;
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const headerHeight = 50;

  return (
    <Box
      aria-hidden={!showScrollingHeader}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        // Always height 0 + overflow visible so the header overlays content
        // instead of pushing it down. Changing height would shift the sentinel
        // observed by IntersectionObserver, causing an infinite show/hide loop
        // (visible as flickering on mobile/Cordova slow-scroll).
        overflow: 'visible',
        height: 0,
        pointerEvents: showScrollingHeader ? 'auto' : 'none',
        flexShrink: 0,
      }}
    >
      <Box
        component={motion.header}
        initial={{ y: '-100%' }}
        animate={{ y: showScrollingHeader ? 0 : '-100%' }}
        transition={{
          type: 'tween',
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'other.outlineBorder',
          px: { xs: 1.5, md: 3 },
          py: 1.25,
          height: headerHeight,
          overflowX: 'auto',
          overflowY: 'hidden',
          // Keep the row on one line and allow panning when controls exceed viewport width.
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            minWidth: 'max-content',
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'baseline',
              gap: { xs: 1, md: 2 },
              flexShrink: 0,
            }}
          >
            <Typography
              sx={(t) => ({
                fontSize: t.typography.pxToRem(16),
                fontWeight: t.typography.fontWeightMedium,
                color: 'text.primary',
              })}
            >
              {title}
            </Typography>
            {isMobile ? (
              <IconButton
                aria-label="Back to top"
                onClick={scrollToTop}
                color="primary"
                size="small"
                sx={{ p: 0 }}
              >
                <ArrowUpward sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <Button
                component="button"
                variant="link"
                onClick={scrollToTop}
                size="small"
              >
                Back To Top
                <ArrowUpward sx={{ fontSize: 16, pl: 0.5 }} />
              </Button>
            )}
          </Stack>
          {scrollingHeaderExtra && (
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 2, flexShrink: 0 }}
            >
              {scrollingHeaderExtra}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
